import { getCookie, removeCookie, setSessionCookie } from '@/utils/cookies';
import { getCookieRootDomain } from '@/utils/config';

function generateTabId(): string {
  return `tab_${Date.now()}_${Math.random().toString(36)
    .slice(2, 11)}`;
}

function getTabsFromCookie(tabTrackingCookie: string): string[] {
  const tabsCookie = getCookie(tabTrackingCookie);

  if (typeof tabsCookie !== 'string') {
    return [];
  }

  try {
    const tabs = JSON.parse(tabsCookie) as unknown;

    if (Array.isArray(tabs)) {
      return tabs.filter((tab): tab is string => typeof tab === 'string');
    }

    return [];
  } catch {
    return [];
  }
}

interface TabOptions {
  readonly cookieName: string;
  readonly tabId: string;
  readonly domain: string;
  readonly isSecure: boolean;
}

function registerTab(options: TabOptions): void {
  const { cookieName, tabId } = options;
  const tabTrackingCookie = `${cookieName}_tabs`;
  const existingTabs = getTabsFromCookie(tabTrackingCookie);
  const updatedTabs = [...new Set([...existingTabs, tabId])];

  // Store tab list in a cookie (accessible cross-domain)
  setSessionCookie(tabTrackingCookie, JSON.stringify(updatedTabs));
}

function unregisterTab(options: TabOptions): void {
  const { cookieName, tabId, domain, isSecure } = options;
  const tabTrackingCookie = `${cookieName}_tabs`;
  const existingTabs = getTabsFromCookie(tabTrackingCookie);
  const remainingTabs = existingTabs.filter(id => id !== tabId);

  if (remainingTabs.length === 0) {
    // Last tab closing - remove the main cookie and tab tracking
    removeCookie(cookieName, {
      domain,
      secure: isSecure,
      sameSite: 'lax'
    });
    removeCookie(tabTrackingCookie, {
      domain,
      secure: isSecure,
      sameSite: 'lax'
    });
  } else {
    setSessionCookie(tabTrackingCookie, JSON.stringify(remainingTabs));
  }
}

/*
 * Cross-domain tab session management - clears cookie when all tabs are closed
 * The main cookie: impact_ref_id is being set and filled with Impact im_ref query param value
 * Tab aware cookie: impact_ref_id_tabs is being set as array of tab ids,
 * which is used to track every tab being opened and closed (beforeunload/pagehide).
 * Using session cookie means on browser close, the cookies are removed.
 * Once all tabs are closed, the main cookie is removed and the tab tracking cookie is removed
 * (unregisterTab handles this).
 */
export function setTabAwareCookie(name: string, value: string): string | undefined {
  // Set the main cookie as a session cookie
  const result = setSessionCookie(name, value);
  const tabId = generateTabId();
  const domain = getCookieRootDomain();
  const isSecure = window.location.protocol === 'https:';

  registerTab({
    cookieName: name,
    tabId,
    domain,
    isSecure
  });

  const cleanup = () => {
    unregisterTab({
      cookieName: name,
      tabId,
      domain,
      isSecure
    });
  };

  window.addEventListener('pagehide', cleanup);

  return result;
}
